import React, { useEffect, useState } from 'react';
import { Button, Box, Container, Paper, Typography, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ReusableDataGrid from '../../../components/ReusableDataGrid';
import * as invoiceService from '../services/invoiceService';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import jsPDF from 'jspdf';
import { getCompanyInfo } from "../../../services/authService";
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';

const ListInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvoices();
    fetchCompanyData();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const data = await invoiceService.getInvoices();
      
      const invoicesArray = Array.isArray(data?.payload)
        ? data.payload
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      
      setInvoices(invoicesArray);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const data = await getCompanyInfo();
      if (data) {
        setCompanyInfo({
          name: data.CompanyName || '',
          vat: data.BTWNumber || '',
          email: data.Email || '',
          phone: data.PhoneNumber || '',
          website: data.Website || '',
          address: data.Address || '',
          city: data.City || '',
          postCode: data.PostCode || '',
          country: data.Country || '',
          iban: data.IBANumber || '',
        });
        console.log('✅ Company info loaded for invoices');
      }
    } catch (error) {
      console.error('❌ Error fetching company info:', error);
      toast.warning('Failed to load company information');
    }
  };

  // ✅ Helper function to format date
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // ✅ Helper function to fill template with invoice data
  const getFilledTemplate = (templateHtml, invoice) => {
    const lineItems = invoice.LineItems || [];
    const totalQuantity = lineItems.reduce((sum, item) => sum + Number(item.Quantity || 0), 0);
    const totalExcl = lineItems.reduce((sum, item) => sum + Number(item.TotalExcl || 0), 0);
    
    // Calculate VAT using invoice-level VAT percentage
    const vatPercentage = Number(invoice.VATPercentage || 21);
    const totalVAT = (totalExcl * vatPercentage) / 100;
    const totalAmount = totalExcl + totalVAT;
    
    const paidAmount = Number(invoice.InvoicePaidAmount || 0);
    const dueAmount = totalAmount - paidAmount;

    // Extract customer data from Customer object
    const customer = invoice.Customer || {};
    const customerName = customer.Name || invoice.CustomerName || '';
    const customerVAT = customer.VATNumber || invoice.CustomerVAT || '';
    const customerAddress = customer.Street || invoice.CustomerAddress || '';
    const customerCity = customer.City || invoice.CustomerCity || '';
    const customerPostCode = customer.PostCode || '';
    const customerCountry = customer.Country || '';
    const customerEmail = customer.Email || '';
    const customerPhone = customer.PhoneNumber || customer.MobilePhone || '';

    // Get invoice date from first line item's CreatedDate
    const invoiceDate = lineItems.length > 0 && lineItems[0].CreatedDate 
      ? lineItems[0].CreatedDate 
      : invoice.InvoiceDate || invoice.CreatedDate || new Date().toISOString();

    // Calculate due date (e.g., 30 days from invoice date)
    const dueDate = invoice.DueDate || (() => {
      const invoiceDateObj = new Date(invoiceDate);
      invoiceDateObj.setDate(invoiceDateObj.getDate() + 30);
      return invoiceDateObj.toISOString();
    })();

    // Determine payment status
    let paymentStatus = 'Not Paid';
    if (paidAmount >= totalAmount) paymentStatus = 'Paid';
    else if (paidAmount > 0 && paidAmount < totalAmount) paymentStatus = 'Partially Paid';

    // Generate product rows
    const ProductRows = lineItems.map(item => `
      <tr>
        <td>${item.Reference || item.ProductId || ''}</td>
        <td class="text-center">${item.Quantity || 0}</td>
        <td>${item.Description || item.Name || ''}</td>
        <td class="text-right">€${Number(item.UnitPrice || item.Price || 0).toFixed(2)}</td>
        <td class="text-right">€${Number(item.TotalExcl || 0).toFixed(2)}</td>
        <td class="text-right">€${(Number(item.TotalExcl || 0) * (1 + vatPercentage / 100)).toFixed(2)}</td>
      </tr>
    `).join('');

    // Generate VAT section
    const VATSection = vatPercentage > 0
      ? `
      <div>
        <span>VAT ${vatPercentage}%: €${totalExcl.toFixed(2)}</span>
        <span>€${totalVAT.toFixed(2)}</span>
      </div>
      `
      : `<div><span>VAT 0%:</span><span>€0.00</span></div>`;

    // Replace all placeholders
    let filledTemplate = templateHtml
      // Company Info - Using fetched company data
      .replace(/{{CompanyName}}/g, companyInfo?.name || 'Your Company Name')
      .replace(/{{CompanyVAT}}/g, companyInfo?.vat || '')
      .replace(/{{CompanyEmail}}/g, companyInfo?.email || '')
      .replace(/{{CompanyPhone}}/g, companyInfo?.phone || '')
      .replace(/{{CompanyWebsite}}/g, companyInfo?.website || '')
      .replace(/{{CompanyAddress}}/g, companyInfo?.address ? `${companyInfo.address}${companyInfo.postCode ? ', ' + companyInfo.postCode : ''}${companyInfo.city ? ' ' + companyInfo.city : ''}` : '')
      .replace(/{{CompanyBankDetail}}/g, companyInfo?.iban || '')
      
      // Customer Info - Using Customer object
      .replace(/{{CustomerName}}/g, customerName)
      .replace(/{{CustomerVAT}}/g, customerVAT)
      .replace(/{{CustomerAddress}}/g, customerAddress)
      .replace(/{{CustomerCity}}/g, customerCity ? `${customerPostCode} ${customerCity}` : '')
      .replace(/{{CustomerEmail}}/g, customerEmail)
      .replace(/{{CustomerPhone}}/g, customerPhone)
      .replace(/{{CustomerCountry}}/g, customerCountry)
      
      // Invoice Info - Using line item's CreatedDate
      .replace(/{{InvoiceNumber}}/g, invoice.InvoiceNumber || '')
      .replace(/{{InvoiceDate}}/g, formatDate(invoiceDate))
      .replace(/{{DueDate}}/g, formatDate(dueDate))
      
      // Product Info
      .replace(/{{ProductRows}}/g, ProductRows)
      .replace(/{{TotalQuantity}}/g, totalQuantity.toString())
      .replace(/{{DiscountRow}}/g, '')
      
      // Financial Totals
      .replace(/{{TotalExcl}}/g, totalExcl.toFixed(2))
      .replace(/{{TotalVAT}}/g, totalVAT.toFixed(2))
      .replace(/{{TotalAmount}}/g, totalAmount.toFixed(2))
      .replace(/{{TotalIncl}}/g, totalAmount.toFixed(2))
      .replace(/{{GrandTotal}}/g, totalAmount.toFixed(2))
      .replace(/{{InvoicePaidAmount}}/g, paidAmount.toFixed(2))
      .replace(/{{InvoiceDueAmount}}/g, dueAmount.toFixed(2))
      
      // VAT Section
      .replace(/{{VATSection}}/g, VATSection)
      .replace(/{{VAT}}/g, vatPercentage.toString())
      
      // Payment Status
      .replace(/{{PaymentStatus}}/g, paymentStatus)
      
      // Peppol Info
      .replace(/{{PeppolStatus}}/g, invoice.PeppolStatus || 'Not Sent')
      .replace(/{{PeppolMessageId}}/g, invoice.PeppolMessageId || '')
      
      // Other placeholders
      .replace(/{{OutstandingSection}}/g, '')
      .replace(/{{CustomTextSection}}/g, invoice.CustomText ? `<div style="margin-top: 20px; padding: 15px; background: #f9f9f9; border-left: 4px solid #667eea; border-radius: 5px;"><strong style="color: #667eea;">Additional Notes:</strong><br><div style="margin-top: 8px; line-height: 1.6;">${invoice.CustomText}</div></div>` : '')
      .replace(/{{PageNumber}}/g, '1')
      .replace(/{{TotalPages}}/g, '1');

    return filledTemplate;
  };

  // ✅ Updated PDF generation using html2canvas (same as AddInvoicePage)
  const handlePreviewInvoicePdf = async (invoice) => {
    try {
      // Check if company info is loaded
      if (!companyInfo) {
        toast.warning('Loading company information...');
        await fetchCompanyData();
        // Wait a bit for state to update
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Fetch template
      const templateHtml = await fetch('/invoicetemplates/template-en.html').then((res) => res.text());
      const filledTemplate = getFilledTemplate(templateHtml, invoice);

      // Create temporary div
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = filledTemplate;
      
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = '794px';
      tempDiv.style.background = 'white';
      document.body.appendChild(tempDiv);

      // Wait for images to load
      const images = tempDiv.getElementsByTagName('img');
      const imagePromises = Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          const timeout = setTimeout(() => resolve(), 3000);
          img.onload = () => {
            clearTimeout(timeout);
            resolve();
          };
          img.onerror = () => {
            clearTimeout(timeout);
            resolve();
          };
        });
      });

      await Promise.all(imagePromises);

      // Generate canvas
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        width: 794,
        windowWidth: 794,
        backgroundColor: '#ffffff',
        onclone: (clonedDoc) => {
          clonedDoc.body.style.background = 'white';
        }
      });

      document.body.removeChild(tempDiv);

      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error('Invalid canvas generated');
      }

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      if (imgHeight > pdfHeight) {
        const scaledWidth = (canvas.width * pdfHeight) / canvas.height;
        const scaledHeight = pdfHeight;
        pdf.addImage(imgData, 'PNG', 0, 0, scaledWidth, scaledHeight);
      } else {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      }

      // Open in new tab
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      
      if (error.message.includes('Invalid canvas')) {
        toast.error('Failed to render invoice');
      } else if (error.message.includes('html2canvas')) {
        toast.error('Failed to convert HTML');
      } else {
        toast.error('Failed to generate PDF. Please try again.');
      }
    }
  };

  const columns = [
    { field: 'Id', headerName: 'ID', flex: 0.5, minWidth: 80 },
    {
      field: 'CustomerId',
      headerName: 'Customer',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (params.row.CustomerName ? params.row.CustomerName : '-'),
    },
    { field: 'InvoiceNumber', headerName: 'Invoice Number', flex: 1, minWidth: 150 },
    { 
      field: 'InvoicePaidAmount', 
      headerName: 'Paid Amount', 
      flex: 1, 
      align: 'center',
      minWidth: 120,
      headerAlign: 'center',
      renderCell: (params) => {
        const paidAmount = Number(params.row.InvoicePaidAmount || 0);
        return (
          <Typography variant="body2" fontWeight={500}>
            €{paidAmount.toFixed(2)}
          </Typography>
        );
      }
    },
    {
      field: 'ProductCount',
      headerName: 'Items',
      flex: 0.6,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (params.row.LineItems ? params.row.LineItems.length : 0),
    },
    {
      field: 'IsSendOnPeppol',
      headerName: 'Send on Peppol',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isSendOnPeppol = params.row.IsSendOnPeppol;
        return (
          <Chip
            icon={isSendOnPeppol ? <CheckCircleIcon /> : <CancelIcon />}
            label={isSendOnPeppol ? 'Yes' : 'No'}
            color={isSendOnPeppol ? 'success' : 'default'}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: 11,
              minWidth: 70,
            }}
          />
        );
      },
    },
    {
      field: 'PeppolStatus',
      headerName: 'Peppol Status',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isSendOnPeppol = params.row.IsSendOnPeppol;
        const peppolStatus = params.row.PeppolStatus || 'Not Sent';
        
        let icon;
        let color;
        let label;

        if (!isSendOnPeppol) {
          icon = <CancelIcon />;
          color = 'default';
          label = 'N/A';
        } else {
          switch (peppolStatus.toLowerCase()) {
            case 'sent':
            case 'delivered':
            case 'success':
              icon = <CheckCircleIcon />;
              color = 'success';
              label = 'Sent';
              break;
            case 'pending':
            case 'processing':
              icon = <PendingIcon />;
              color = 'warning';
              label = 'Pending';
              break;
            case 'failed':
            case 'error':
              icon = <CancelIcon />;
              color = 'error';
              label = 'Failed';
              break;
            default:
              icon = <PendingIcon />;
              color = 'info';
              label = 'Not Sent';
          }
        }

        return (
          <Chip
            icon={icon}
            label={label}
            color={color}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: 11,
              minWidth: 85,
            }}
          />
        );
      },
    },
    {
      field: 'InvoiceUrl',
      headerName: 'PDF',
      flex: 0.5,
      minWidth: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.row ? (
          <Box
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                transform: 'scale(1.1)',
                transition: 'transform 0.2s'
              }
            }}
            title="Preview PDF"
            onClick={(e) => {
              e.stopPropagation();
              handlePreviewInvoicePdf(params.row);
            }}
          >
            <PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: 28 }} />
          </Box>
        ) : null,
    },
    {
      field: 'Status',
      headerName: 'Payment Status',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const paid = Number(params.row.InvoicePaidAmount) || 0;
        const total = Number(params.row.InvoicePrice) || 0;
        let label = '';
        let bg = '';
        if (paid === 0) {
          label = 'Not Paid';
          bg = '#d32f2f';
        } else if (paid >= total) {
          label = 'Paid';
          bg = '#388e3c';
        } else {
          label = 'Partial';
          bg = '#fbc02d';
        }
        return (
          <span
            style={{
              color: '#fff',
              background: bg,
              borderRadius: 10,
              padding: '1px 8px',
              fontWeight: 500,
              fontSize: 11,
              display: 'inline-block',
              minWidth: 60,
              lineHeight: 1.6,
              textAlign: 'center',
              letterSpacing: 0.2,
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Button 
          size="small"
          variant="outlined"
          onClick={() => navigate(`/invoices/view/${params.row.Id}`)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.2) 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ color: '#667eea' }}>
                Invoices
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and track all your invoices
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={() => navigate('/invoices/add')}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.2s',
              },
            }}
          >
            Add Invoice
          </Button>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <ReusableDataGrid
          rows={invoices}
          columns={columns}
          getRowId={(row) => row.Id}
          loading={loading}
          checkboxSelection
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          pagination
          disableRowSelectionOnClick
          autoHeight
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(102, 126, 234, 0.04)',
            },
          }}
        />
      </Paper>
    </Container>
  );
};

export default ListInvoices;